import { Modal, Button, IconButton, Input } from "@mui/material";
import { Dispatch, SetStateAction } from "react";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import CloseIcon from "@mui/icons-material/Close";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { isString, isValidInput } from "../utils/validations";
import { RoleTypes } from "../types/General";

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  handleSubmit: () => void;
  profileImage: string;
  setProfileImage: Dispatch<SetStateAction<string>>;
  handleProfileUpload: (e: any) => void;
  firstName: string;
  setFirstName: Dispatch<SetStateAction<string>>;
  error: boolean;
  middleName: string;
  setMiddleName: Dispatch<SetStateAction<string>>;
  lastName: string;
  setLastName: Dispatch<SetStateAction<string>>;
  email: string;
  setEmail: Dispatch<SetStateAction<string>>;
  phone: string;
  dialCode: string;
  handleChangePhone: (phone: string, country: any) => void;
  setError: Dispatch<SetStateAction<boolean>>;
  roles: RoleTypes[];
  selectedRole: number | null;
  setSelectedRole: Dispatch<SetStateAction<number | null>>;
  selectedId: number | null;
}

export default function AddUser({
  open,
  onClose,
  setOpen,
  handleSubmit,
  profileImage,
  setProfileImage,
  handleProfileUpload,
  firstName,
  setFirstName,
  error,
  middleName,
  setMiddleName,
  lastName,
  setLastName,
  email,
  setEmail,
  phone,
  dialCode,
  handleChangePhone,
  setError,
  roles,
  selectedRole,
  setSelectedRole,
  selectedId,
}: AddUserModalProps) {
  return (
    <Modal
      className="modal addUser_modal"
      id="addUserModal"
      aria-labelledby="addUser-modal-title"
      aria-describedby="addUser-modal-description"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="btn-close" onClick={onClose}>
            <CloseIcon />
          </div>
          <div className="modal_title">
            <h2>Add Sub-admin</h2>
            <p>Send access to a sub-admin by filling the below details</p>
          </div>
          <form className="form" action="">
            <div className="gap_p">
              <div className="control_group w_100">
                <div className="upload_box">
                  <label>
                    {profileImage && (
                      <IconButton
                        className="crossIcon"
                        onClick={() => setProfileImage("")}
                      >
                        <CloseIcon />
                      </IconButton>
                    )}
                    <figure>
                      <img
                        src={
                          profileImage
                            ? profileImage
                            : "/static/images/user_placeholder.png"
                        }
                        alt="Upload"
                      />
                    </figure>
                    <IconButton className="uploadIcon">
                      <label htmlFor="icon-button-file">
                        <Button component="span" className="upload_image_btn">
                          <img
                            src="/static/images/edit2_icon.svg"
                            alt="Upload"
                          />
                          <Input
                            sx={{ display: "none" }}
                            id="icon-button-file"
                            type="file"
                            inputProps={{
                              accept: "image/png,image/jpeg,image/jpg",
                            }}
                            onChange={(e: any) => {
                              handleProfileUpload(e);
                              e.target.value = null;
                            }}
                          />
                        </Button>
                      </label>
                    </IconButton>
                  </label>
                </div>
              </div>
              <div className="control_group w_50">
                <label>First Name</label>
                <TextField
                  variant="outlined"
                  placeholder="First Name"
                  fullWidth
                  hiddenLabel
                  value={firstName}
                  onChange={(val) => {
                    if (
                      isValidInput(val.target.value) &&
                      isString(val.target.value)
                    ) {
                      setFirstName(val.target.value);
                    }
                  }}
                  inputProps={{ maxLength: 30 }}
                />
                {error && !firstName?.length ? (
                  <h6 className="err_msg">First name is required</h6>
                ) : error && firstName?.length <= 2 ? (
                  <h6 className="err_msg">
                    First name must be atleast 3 characters long
                  </h6>
                ) : null}
              </div>
              <div className="control_group w_50">
                <label>Middle name</label>
                <TextField
                  variant="outlined"
                  placeholder="Middle name"
                  fullWidth
                  hiddenLabel
                  value={middleName}
                  inputProps={{ maxLength: 30 }}
                  onChange={(val) => {
                    if (
                      isValidInput(val.target.value) &&
                      isString(val.target.value)
                    ) {
                      setMiddleName(val.target.value);
                    }
                  }}
                />
              </div>
              <div className="control_group w_50">
                <label>Last name</label>
                <TextField
                  variant="outlined"
                  placeholder="Last name"
                  fullWidth
                  hiddenLabel
                  value={lastName}
                  inputProps={{ maxLength: 30 }}
                  onChange={(val) => {
                    if (
                      isValidInput(val.target.value) &&
                      isString(val.target.value)
                    ) {
                      setLastName(val.target.value);
                    }
                  }}
                />
              </div>
              <div className="control_group w_50">
                <label>E-mail</label>
                <TextField
                  variant="outlined"
                  placeholder="Enter here"
                  fullWidth
                  hiddenLabel
                  disabled={selectedId ? true : false}
                  value={email}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                      return;
                    } else {
                      setEmail(val.target.value);
                    }
                  }}
                />
                {error && !email?.length ? (
                  <h6 className="err_msg">Email is required</h6>
                ) : error && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? (
                  <h6 className="err_msg">
                    Please enter a valid email address!
                  </h6>
                ) : null}
              </div>
              <div className="control_group w_100">
                <label>Phone Number</label>
                <PhoneInput
                  enableSearch
                  value={dialCode + phone}
                  country={"uae"}
                  inputClass="phoneInput"
                  onChange={(phone, country) =>
                    handleChangePhone(phone, country)
                  }
                  inputStyle={{ width: "100%" }}
                  buttonClass="phoneBtn"
                  placeholder="Phone Number"
                />
                {error && !phone?.length ? (
                  <h6 className="err_msg">Phone number is required</h6>
                ) : error && phone?.length <= 5 ? (
                  <h6 className="err_msg">
                    Phone number must contain atleast 6 digits
                  </h6>
                ) : null}
              </div>
            </div>
            <h3>Role</h3>
            <ul className="roles_list">
              <RadioGroup
                value={selectedRole}
                onChange={(e) => setSelectedRole(Number(e.target.value))}
              >
                {roles?.length
                  ? roles?.map((role) => (
                      <FormControlLabel
                        key={role.id}
                        value={role.id}
                        control={<Radio />}
                        label={role.role_name}
                      />
                    ))
                  : null}
              </RadioGroup>
            </ul>
            <div className="footer_btn_flex">
              <Button
                className="btn btn_highlight"
                onClick={() => {
                  setError(false);
                  setOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                className="btn btn_secondary"
                onClick={() => {
                  setError(true);
                  handleSubmit();
                }}
              >
                Submit
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
